import { selectModeType } from 'util/selector/querySelector';

import { createSelector } from '@reduxjs/toolkit';
import { selectHasUnrestrictedCrossFilteringPermission } from 'reduxStore/user/selectors';
import { AppState } from 'reduxStore/appStore';
import {
  selectIsDepartmentIdSelected,
  selectSelectedDepartmentIds,
} from 'reduxStore/departments/selectors';
import {
  selectGroupedClassifications,
  selectSelectedClassifications,
} from 'reduxStore/demography/selectors';
import { Filtering } from 'model/Filtering';
import { FilteringState } from 'reduxStore/filtering/initialState';
import { selectIsLifecycleProjectKind } from 'reduxStore/project/selectors';
import { ModeType } from 'register/ModeType';
import { DemographyClassification } from 'model/DemographyClassification';

export const selectCanApplyFilter = (command: Filtering.ChangeFilteringCommand) =>
  createSelector(
    selectHasUnrestrictedCrossFilteringPermission,
    selectSelectedClassifications,
    selectSelectedDepartmentIds,
    (unrestrictedFiltering, demographics, departmentIds) => {
      if (unrestrictedFiltering) {
        return true;
      }

      return Filtering.canApplyFilter({
        command,
        selectedDemographics: demographics,
        selectedDepartments: departmentIds,
      });
    }
  );

export const selectCanFilterByDepartment = createSelector(
  selectHasUnrestrictedCrossFilteringPermission,
  selectSelectedClassifications,
  (unrestrictedFiltering, demographics) => {
    if (unrestrictedFiltering) {
      return true;
    }

    return demographics.length === 0;
  }
);

export const selectIsFilterSelected = (command: Filtering.ChangeFilteringCommand) => (
  state: AppState
): boolean => {
  switch (command.type) {
    case 'DEPARTMENT':
      return selectIsDepartmentIdSelected(command.departmentId)(state);
    case 'DEMOGRAPHY':
      return selectIsDemographicSelected(command.classification)(state);
    default:
      throw new Error('UNKNOWN COMMAND TYPE');
  }
};

export function selectDomain(state: AppState): FilteringState.Domain {
  return state.filtering || FilteringState.INITIAL_DOMAIN;
}

export const selectAggregationPeriod = createSelector(
  selectDomain,
  (domain) => domain.aggregationPeriod
);

export const selectSelectedDemographics = createSelector(
  selectDomain,
  (domain) => domain && domain.selectedClassifications
);

export const selectIsDemographicSelected = (classification: DemographyClassification) =>
  createSelector(
    selectSelectedDemographics,
    (selectedClassifications) =>
      !!selectedClassifications.find((selectedClassification) =>
        DemographyClassification.hasEqualIds(classification, selectedClassification)
      )
  );

// Returns true if non-lifecycle project, no demography selected, 0 or 1 department selected
export const selectIsVisibleDownloadAllOption = createSelector(
  selectIsLifecycleProjectKind,
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  (isLifecycleProjectKind, demographics, departmentIds) =>
    !isLifecycleProjectKind && Object.keys(demographics).length < 1 && departmentIds.length <= 1
);

export const selectSelectedFiltersLength = createSelector(
  selectSelectedDepartmentIds,
  selectSelectedClassifications,
  (departments, demographics) => {
    return departments.length + demographics.length;
  }
);

export const MAXIMUM_SELECTED_FILTERS = 10;
// Should show download PDFs and PPTs;
export const selectShouldShowDownloadButton = createSelector(
  (state) => selectModeType(state),
  selectSelectedFiltersLength,
  (modeType, selectedFiltersLength: number) =>
    modeType === ModeType.COMBINED || selectedFiltersLength <= MAXIMUM_SELECTED_FILTERS
);
